import {
  Direction,
  getHighlightingDetails,
  type Process,
  type ProcessLayoutMode
} from "@certa/studio-core";
import type {
  CallbackPayload,
  CustomPointerEvent,
  HTMLDragPayload
} from "../engines/interactionEngine/interactionTypes";
import type { FSC } from "../engines/renderingEngine/fsc.types";
import { BoxNode } from "../engines/renderingEngine/primitiveNodes";
import { HTMLToText } from "../utils/text";
import { AddNode } from "./AddNode";
import { AddSwimlane } from "./AddSwimlane";
import { FieldNode } from "./FieldNode";
import { StepNode } from "./StepNode";
import { SwimlaneNode } from "./SwimlaneNode";
import { THEME } from "./colorPalette.constants";
import { styles } from "./scomponents.styles";
import { DragType } from "../engines/interactionEngine/interactionObserverConstants";
import { ComponentType } from "../..";
import type { ChangedComponentsByChangeType } from "@certa/studio/api";
import { getHighlightingStyle } from "./scomponents.utils";
import { getDragContext } from "../engines/interactionEngine/dragContext";
import {
  getAddFieldNodeKey,
  getAddStepNodeKey,
  getAddSwimlaneNodeKey,
  getFieldNodeKey,
  getProcessNodeKey,
  getStepNodeKey,
  getSwimlaneNodeKey
} from "../utils/general.utils";
import { VerticalDropIndicator } from "./VerticalDropIndicator";
import { NodeType } from "./scomponents.constants";
import {
  handleDragStart,
  handleOnDrop,
  handleOnDropping,
  handleOnDroppingOut
} from "./commonEventHandlers";
import type { StudioTemplateTypes } from "@certa/types";
import { HORIZONTAL_DROP_INDICATOR_HEIGHT } from "./HorizontalDropIndicator";
import { NoChildren } from "./NoChildren";
import { EmptyProcess } from "./EmptyProcess";
import type { FlowLineRuleDetails } from "@certa/studio/src/hooks/processLookup/flowLayoutCalculator";

export type ProcessNodeProps = {
  process: Process | null;
  readonly: boolean;
  theme: THEME;
  collapsed: string[];
  onCollapseToggle?: (params: { nodeId: string; collapse: boolean }) => void;

  onNewNode: (
    props: {
      type: NodeType;
      parentId: string;
      index?: number;
    } & Partial<Pick<HTMLDragPayload, "subType">>
  ) => void;
  onNodeMoved: (props: {
    type: NodeType;
    nodeId: string;
    sourceParentId: string;
    targetParentId: string;
    index: number;
  }) => void;

  selectedKey: string;
  onSelect?: (nodeId: string, skipDrawerOpen?: boolean) => void;
  onDeselect?: () => void;
  onComponentDrag: (key: string) => void;
  onPointerOver: (componentKey: string) => void;
  onPointerOut: () => void;
  componentsToHighlight: ChangedComponentsByChangeType;

  // Triggered on node that's being dragged (This should support HTML Drags as well as PIXI Drags)
  onDragStart?: (event: CustomPointerEvent) => boolean; // True allows drag, false prevents drag
  // onDropping?: (event: CustomPointerEvent) => boolean; // True allows drop, false prevents drop

  // Triggered on node that the dragged node is being dropped over
  onDroppedOver?: (event: CustomPointerEvent) => void;

  onDropping?: (key: string) => void;
  onDroppingOut?: (key: string) => void;
  // NodeId which is currently being hovered over during onDropping event.
  currentDroppingId: string;
  highlightedKey: string;
  openRuleDrawer: (
    componentType: ComponentType,
    componentId: string,
    direction: Direction,
    ruleDetails?: Readonly<FlowLineRuleDetails[]>,
    dependentStepIds?: Readonly<string[]>
  ) => void;
  getRuleIndicatorCount: (componentId: string) => {
    upStreamRulesCount: number;
    downStreamRulesCount: number;
  };

  // templates callbacks
  onTemplateDrop: ({
    templateId,
    templateType,
    componentId,
    position,
    parentId
  }: {
    templateId: string;
    templateType: StudioTemplateTypes;
    position: number;
    parentId?: string;
    componentId?: string;
  }) => void;

  layoutMode: ProcessLayoutMode;

  //SDO
  isSDOEnabled: boolean;
  getObjectFieldTypeIconForObjectBackedField?: (
    dataObjectMappingTag: string
  ) => string;
};

const ADD_FIELD_DROP_TYPES = [DragType.FIELD, DragType.NEW_FIELD];
const ADD_STEP_DROP_TYPES = [DragType.STEP, DragType.TEMPLATE_STEP];
const AUTO_EXPAND_STEP_DROP_TYPES = [DragType.FIELD, DragType.NEW_FIELD];
const AUTO_EXPAND_SWIMLANE_DROP_TYPES = [
  DragType.STEP,
  DragType.FIELD,
  DragType.NEW_FIELD,
  DragType.TEMPLATE_STEP
];

export const ProcessNode: FSC<ProcessNodeProps> = ({
  process,
  collapsed,
  onCollapseToggle,
  selectedKey,
  onSelect,
  onDeselect,
  theme = THEME.LIGHT,
  onNewNode,
  onNodeMoved,
  currentDroppingId,
  onDropping,
  onDroppingOut,
  highlightedKey,
  readonly,
  openRuleDrawer,
  getRuleIndicatorCount,
  onComponentDrag,
  componentsToHighlight,
  onTemplateDrop,
  isSDOEnabled,
  getObjectFieldTypeIconForObjectBackedField
}) => {
  // If Process is empty or process object not loaded in memory yet
  if (!process || !process.process.swimlaneSequence) {
    return BoxNode({
      key: getProcessNodeKey("Empty"),
      style: {},
      children: []
    });
  }

  const handleCollapseToggle = (key: string, event: CallbackPayload): void => {
    onCollapseToggle?.({
      nodeId: key,
      collapse: !collapsed.includes(key)
    });
  };

  const style = styles[theme];

  const dragContext = getDragContext();
  const isDragging =
    // dragContext.node is not null when a node is being dragged within WebGL
    // dragContext.store.dragType is not null when a node is being dragged from HTML
    dragContext.node !== null || dragContext.store.dragType !== null;
  let droppingOverSwimlaneIndex = -1;

  process.process.swimlaneSequence.forEach((swimlaneId, swimlaneIndex) => {
    if (
      isDragging &&
      dragContext.store.droppingOverId === getSwimlaneNodeKey(swimlaneId) &&
      dragContext.store.dragType === DragType.SWIMLANE
    ) {
      droppingOverSwimlaneIndex = swimlaneIndex;
    }
  });

  if (
    isDragging &&
    droppingOverSwimlaneIndex === -1 &&
    dragContext.store.droppingOverId ===
      getAddSwimlaneNodeKey(process.process.id) &&
    dragContext.store.dragType === DragType.SWIMLANE
  ) {
    droppingOverSwimlaneIndex = process.process.swimlaneSequence.length;
  }

  if (
    droppingOverSwimlaneIndex !== -1 &&
    droppingOverSwimlaneIndex !== process.process.swimlaneSequence.length
  ) {
    // We need to make sure that if the dragged item's source index is less
    // than the target index, then we need to adjust drop indicator,
    // such that it shows as dropping "below" the target index. otherwise
    // it should show as dropping "above" the target index.
    if (
      dragContext.store.sourceIndex < droppingOverSwimlaneIndex &&
      dragContext.store.droppingOverId !==
        getAddSwimlaneNodeKey(process.process.id)
    ) {
      // Dragged item is above the target index

      droppingOverSwimlaneIndex = droppingOverSwimlaneIndex + 1;
    }
  }

  const children = process.process.swimlaneSequence.map(
    (swimlaneId, swimlaneIndex) => {
      const swimlane = process.swimlanes[swimlaneId];

      if (!swimlane)
        /**
        * Temp fix for the issue where the new components are added but their references
          are not found in the process. Eg- happening for add_intents only i'e 
          additions to existing process
        */
        return BoxNode({
          children: [],
          key: getSwimlaneNodeKey("Empty"),
          style: {}
        });
      const swimlaneKey = getSwimlaneNodeKey(swimlane.id);

      const {
        upStreamRulesCount: swimlaneUpStreamCount,
        downStreamRulesCount: swimlaneDownStreamCount
      } = getRuleIndicatorCount(swimlane.id);

      const swimlaneComponentAction = getHighlightingDetails(
        swimlane.tag,
        componentsToHighlight
      );
      const swimlaneStyleOverride = getHighlightingStyle(
        swimlaneComponentAction?.changeType,
        theme
      );

      let droppingOverStepIndex = -1;

      if (
        isDragging &&
        dragContext.store.droppingOverId === getSwimlaneNodeKey(swimlaneId) &&
        (dragContext.store.dragType === DragType.STEP ||
          dragContext.store.dragType === DragType.TEMPLATE_STEP)
      ) {
        droppingOverStepIndex = swimlane.steps.length;
      } else {
        swimlane.steps.forEach((stepId, stepIndex) => {
          if (
            isDragging &&
            dragContext.store.droppingOverId === getStepNodeKey(stepId) &&
            (dragContext.store.dragType === DragType.STEP ||
              dragContext.store.dragType === DragType.TEMPLATE_STEP)
          ) {
            droppingOverStepIndex = stepIndex;
          }
        });
      }

      if (
        isDragging &&
        droppingOverStepIndex === -1 &&
        dragContext.store.droppingOverId === getAddStepNodeKey(swimlane.id) &&
        (dragContext.store.dragType === DragType.STEP ||
          dragContext.store.dragType === DragType.TEMPLATE_STEP)
      ) {
        droppingOverStepIndex = swimlane.steps.length;
      }
      if (
        droppingOverStepIndex !== -1 &&
        droppingOverStepIndex !== swimlane.steps.length
      ) {
        // if we're just reordering steps within the same swimlane, then we need to make sure that
        // if the dragged item's source index is less than the target index, then we need to adjust
        // drop indicator, such that it shows as dropping "below" the target index. otherwise
        // it should show as dropping "above" the target index.
        if (dragContext.store.sourceParentId === swimlaneId) {
          // We're just rearranging fields within the same step
          if (dragContext.store.sourceIndex < droppingOverStepIndex) {
            // Dragged item is above the target index

            droppingOverStepIndex = droppingOverStepIndex + 1;
          }
        }
      }

      const steps = swimlane.steps.map((stepId, stepIndex) => {
        const step = process.steps[stepId];

        if (!step)
          return BoxNode({
            children: [],
            key: getStepNodeKey("Empty"),
            style: {}
          });
        const stepKey = getStepNodeKey(step.id);

        const {
          upStreamRulesCount: stepUpStreamCount,
          downStreamRulesCount: stepDownStreamCount
        } = getRuleIndicatorCount(step.id);

        const stepComponentAction = getHighlightingDetails(
          step.tag,
          componentsToHighlight
        );
        const stepStyleOverride = getHighlightingStyle(
          stepComponentAction?.changeType,
          theme
        );

        let droppingOverFieldIndex = -1;

        if (
          isDragging &&
          dragContext.store.droppingOverId === getStepNodeKey(stepId) &&
          (dragContext.store.dragType === DragType.FIELD ||
            dragContext.store.dragType === DragType.NEW_FIELD)
        ) {
          droppingOverFieldIndex = step.fields.length;
        } else {
          step.fields.forEach((fieldId, fieldIndex) => {
            if (
              isDragging &&
              dragContext.store.droppingOverId === getFieldNodeKey(fieldId) &&
              (dragContext.store.dragType === DragType.FIELD ||
                dragContext.store.dragType === DragType.NEW_FIELD)
            ) {
              droppingOverFieldIndex = fieldIndex;
            }
          });
        }

        if (
          isDragging &&
          droppingOverFieldIndex === -1 &&
          dragContext.store.droppingOverId === getAddFieldNodeKey(step.id) &&
          (dragContext.store.dragType === DragType.FIELD ||
            dragContext.store.dragType === DragType.NEW_FIELD)
        ) {
          droppingOverFieldIndex = step.fields.length;
        }
        if (
          droppingOverFieldIndex !== -1 &&
          droppingOverFieldIndex !== step.fields.length
        ) {
          // if we're just reordering fields within the same step, then we need to make sure that
          // if the dragged item's source index is less than the target index, then we need to adjust
          // drop indicator, such that it shows as dropping "below" the target index. otherwise
          // it should show as dropping "above" the target index.
          if (dragContext.store.sourceParentId === stepId) {
            // We're just rearranging fields within the same step
            if (dragContext.store.sourceIndex < droppingOverFieldIndex) {
              // Dragged item is above the target index
              droppingOverFieldIndex = droppingOverFieldIndex + 1;
            }
          }
        }

        const fields = step.fields.map((fieldId, fieldIndex) => {
          const field = process.fields[fieldId];

          if (!field)
            return BoxNode({
              children: [],
              key: getFieldNodeKey("Empty"),
              style: {}
            });

          const fieldKey = getFieldNodeKey(field.id);
          const fieldTitle = HTMLToText(field.body);

          const {
            upStreamRulesCount: fieldUpStreamCount,
            downStreamRulesCount: fieldDownStreamCount
          } = getRuleIndicatorCount(field.id);

          const fieldComponentAction = getHighlightingDetails(
            field.tag,
            componentsToHighlight
          );
          const fieldStyleOverride = getHighlightingStyle(
            fieldComponentAction?.changeType,
            theme
          );
          return FieldNode({
            key: fieldKey,
            theme,
            isDroppingAbove: droppingOverFieldIndex === fieldIndex,
            title: fieldTitle,
            selected: selectedKey === fieldKey || highlightedKey === fieldKey,
            stepId: stepKey,
            swimlaneId: swimlaneKey,
            type: field.fieldType,
            isObjectBackedField: field.fieldType === "data_object_mapping",
            objectFieldTypeIcon: getObjectFieldTypeIconForObjectBackedField?.(
              field?.extra?.data_object_mapping
            ),
            upStreamRulesCount: fieldUpStreamCount,
            downStreamRulesCount: fieldDownStreamCount,
            styleOverride: fieldStyleOverride,
            readonly,
            isRequired: !!field.isRequired,
            isHidden: !!field.hidden,
            onUpStreamRuleIndicatorClick: () => {
              onSelect?.(fieldKey, true);
              openRuleDrawer(ComponentType.FIELD, field.id, Direction.INCOMING);
            },
            onDownStreamRuleIndicatorClick: () => {
              onSelect?.(fieldKey, true);
              openRuleDrawer(ComponentType.FIELD, field.id, Direction.OUTGOING);
            },
            onClick: event => {
              // event.__internal.stopPropagation = true;
              onSelect?.(fieldKey);
            },

            onDragStart: event => {
              handleDragStart({
                event,
                sourceId: fieldId,
                sourceParentId: stepId,
                sourceIndex: fieldIndex,
                sourceDragType: DragType.FIELD
              });
              onComponentDrag(fieldKey);
            },
            onDropped: event => {
              const { sourceId, sourceParentId, dragType, subType } =
                event.dragContextStore || {};

              if (dragType === DragType.NEW_FIELD) {
                onNewNode({
                  type: NodeType.FIELD,
                  parentId: stepId,
                  index: fieldIndex,
                  subType
                });
                return;
              }
              if (!sourceId || !sourceParentId) return;

              onNodeMoved({
                type: NodeType.FIELD,
                nodeId: sourceId,
                sourceParentId,
                targetParentId: stepId,
                index: fieldIndex
              });
              // Select the node that was dragged
              const dragContext = getDragContext();
              if (dragContext.node?.props.key) {
                onSelect?.(dragContext.node?.props.key);
              }

              handleOnDrop({ event });
            },
            onDropping: event => {
              return handleOnDropping({
                event,
                key: fieldKey,
                dropType: DragType.FIELD
              });
            },
            onDroppingOut: event => {
              return handleOnDroppingOut({
                event,
                key: fieldKey
              });
            }
          });
        });

        const isStepExpanded = !collapsed.includes(stepKey);
        return StepNode({
          dropIndicatorIndex: droppingOverFieldIndex,
          isDroppingAbove: droppingOverStepIndex === stepIndex,
          key: stepKey,
          theme,
          swimlaneId: swimlaneKey,
          title: step.name,
          expanded: isStepExpanded,
          onCollapseToggle: handleCollapseToggle.bind(null, stepKey),
          upStreamRulesCount: stepUpStreamCount,
          downStreamRulesCount: stepDownStreamCount,
          onUpStreamRuleIndicatorClick: () => {
            onSelect?.(stepKey);
            openRuleDrawer(ComponentType.STEP, step.id, Direction.INCOMING);
          },
          onDownStreamRuleIndicatorClick: () => {
            onSelect?.(stepKey);
            openRuleDrawer(ComponentType.STEP, step.id, Direction.OUTGOING);
          },
          styleOverride: stepStyleOverride,

          addChildNode: readonly
            ? undefined
            : AddNode({
                theme,
                key: getAddFieldNodeKey(step.id),
                title: "Add field",
                style:
                  droppingOverFieldIndex === fields.length
                    ? {
                        ...style.addField.container,
                        marginTop:
                          (style.addField.container.marginTop ?? 0) -
                          HORIZONTAL_DROP_INDICATOR_HEIGHT
                      }
                    : style.addField.container,
                dropTypes: ADD_FIELD_DROP_TYPES,
                onClick: e => {
                  // adding this for STS to stop multiple events being fired. For reason refer the PR by hovering over this comment
                  e.__internal.stopPropagation = true;
                  onNewNode({ type: NodeType.FIELD, parentId: stepId });
                  return;
                },
                onDropped: event => {
                  const { sourceId, sourceParentId, dragType, subType } =
                    event.dragContextStore || {};

                  if (dragType === DragType.NEW_FIELD) {
                    onNewNode({
                      type: NodeType.FIELD,
                      parentId: stepId,
                      subType
                    });
                    return;
                  }
                  if (!sourceId || !sourceParentId) return;

                  if (sourceParentId === stepId) {
                    // When just rearranging fields within the same step
                    // the length will not change, hence we need to adjust the index
                    onNodeMoved({
                      type: NodeType.FIELD,
                      nodeId: sourceId,
                      sourceParentId,
                      targetParentId: stepId,
                      index: fields.length - 1
                    });
                  } else {
                    // When moving field from one step to another
                    // the length will change, hence we need to take current length as index
                    onNodeMoved({
                      type: NodeType.FIELD,
                      nodeId: sourceId,
                      sourceParentId,
                      targetParentId: stepId,
                      index: fields.length
                    });
                  }

                  // Select the node that was dragged
                  const dragContext = getDragContext();
                  if (dragContext.node?.props.key) {
                    onSelect?.(dragContext.node?.props.key);
                  }

                  handleOnDrop({ event });
                },
                onDropping: event => {
                  return handleOnDropping({
                    event,
                    key: getAddFieldNodeKey(step.id),
                    dropType: DragType.FIELD
                  });
                },
                onDroppingOut: event => {
                  return handleOnDroppingOut({
                    event,
                    key: getAddFieldNodeKey(step.id)
                  });
                }
              }),
          children: fields.length
            ? fields
            : [
                readonly
                  ? NoChildren({
                      key: getFieldNodeKey(`Empty-${stepId}`),
                      title: "Empty step"
                    })
                  : BoxNode({
                      key: getFieldNodeKey(`Empty-${stepId}`),
                      style: style.zeroHeight,
                      children: []
                    })
              ],
          readonly,
          selected:
            currentDroppingId === stepKey ||
            selectedKey === stepKey ||
            highlightedKey === stepKey,
          onClick: event => {
            // event.__internal.stopPropagation = true;
            onSelect?.(stepKey);
          },
          onDragStart: event => {
            handleDragStart({
              event,
              sourceId: stepId,
              sourceParentId: swimlaneId,
              sourceIndex: stepIndex,
              sourceDragType: DragType.STEP
            });
            onComponentDrag(stepKey);
          },
          onDropped: event => {
            const { sourceId, sourceParentId, dragType, subType, extras } =
              event.dragContextStore || {};

            if (dragType === DragType.TEMPLATE_STEP) {
              onTemplateDrop({
                templateId: extras?.templateId as string,
                templateType: subType as StudioTemplateTypes,
                parentId: swimlaneId,
                position: stepIndex,
                componentId: stepId
              });
              return;
            }

            if (dragType === DragType.NEW_FIELD) {
              onNewNode({
                type: NodeType.FIELD,
                parentId: stepId,
                subType
              });
              return;
            }

            if (!sourceId || !sourceParentId) return;

            if (dragType === DragType.STEP) {
              onNodeMoved({
                type: NodeType.STEP,
                nodeId: sourceId,
                sourceParentId,
                targetParentId: swimlaneId,
                index: stepIndex
              });
            } else if (dragType === DragType.FIELD) {
              if (sourceParentId === stepId) {
                // Don't allow dropping on the same step, by dropping field
                // on the header
                return;
              }
              onNodeMoved({
                type: NodeType.FIELD,
                nodeId: sourceId,
                sourceParentId,
                targetParentId: stepId,
                index: fields.length
              });
            }

            // Select the node that was dragged
            const dragContext = getDragContext();
            if (dragContext.node?.props.key) {
              onSelect?.(dragContext.node?.props.key);
            }

            handleOnDrop({ event });
          },
          onDropping: event => {
            // On Dropping must check whether the dragged item is a child-type
            // of the current component, in this case only Fields can trigger
            // auto-expansion of collapsed steps.
            const { dragContextStore } = event;

            if (dragContextStore?.sourceParentId === stepId) {
              // Don't allow dropping on the same step, by dropping field
              // on the header
              return;
            }
            let isHandled = false;
            if (
              dragContextStore?.dragType &&
              AUTO_EXPAND_STEP_DROP_TYPES.includes(dragContextStore.dragType) &&
              collapsed.includes(stepKey)
            ) {
              onDropping?.(stepKey);
              isHandled = true;
            }

            const isDropHandled = handleOnDropping({
              event,
              key: stepKey,
              dropType: DragType.STEP
            });

            return isHandled || isDropHandled;
          },
          onDroppingOut: event => {
            onDroppingOut?.(stepKey);

            return handleOnDroppingOut({
              event,
              key: stepKey
            });
          }
        });
      });

      const isSwimlaneExpanded = !collapsed.includes(swimlaneKey);
      return SwimlaneNode({
        dropIndicatorIndex: droppingOverStepIndex,
        key: swimlaneKey,
        isDroppingBefore: droppingOverSwimlaneIndex === swimlaneIndex,
        theme,
        readonly,
        selected:
          currentDroppingId === swimlaneKey ||
          selectedKey === swimlaneKey ||
          highlightedKey === swimlaneKey,
        expanded: isSwimlaneExpanded,
        upStreamRulesCount: swimlaneUpStreamCount,
        downStreamRulesCount: swimlaneDownStreamCount,
        onUpStreamRuleIndicatorClick: () => {
          onSelect?.(swimlaneKey);
          openRuleDrawer(
            ComponentType.SWIMLANE,
            swimlane.id,
            Direction.INCOMING
          );
        },
        styleOverride: swimlaneStyleOverride,

        onDownStreamRuleIndicatorClick: () => {
          onSelect?.(swimlaneKey);
          openRuleDrawer(
            ComponentType.SWIMLANE,
            swimlane.id,
            Direction.OUTGOING
          );
        },
        addChildNode: readonly
          ? undefined
          : AddNode({
              theme,
              key: getAddStepNodeKey(swimlane.id),
              title: "Add step",
              style:
                droppingOverStepIndex === steps.length
                  ? {
                      ...style.addStep.container,
                      marginTop:
                        (style.addStep.container.marginTop ?? 0) -
                        HORIZONTAL_DROP_INDICATOR_HEIGHT
                    }
                  : style.addStep.container,
              dropTypes: ADD_STEP_DROP_TYPES,
              onClick: e => {
                // adding this for STS to stop multiple events being fired. For reason refer the PR by hovering over this comment
                e.__internal.stopPropagation = true;
                onNewNode({ type: NodeType.STEP, parentId: swimlaneId });
              },
              onDropped: event => {
                const { sourceId, sourceParentId, dragType, subType, extras } =
                  event.dragContextStore || {};

                if (dragType === DragType.TEMPLATE_STEP) {
                  // we are getting the last step component id to add the new step below it
                  const lastStepComponentId =
                    swimlane.steps[swimlane.steps.length - 1];

                  //if the swimlane is empty with no steps then the lastStepComponentId will be undefined
                  onTemplateDrop({
                    templateId: extras?.templateId as string,
                    templateType: subType as StudioTemplateTypes,
                    parentId: swimlaneId,
                    position: steps.length,
                    componentId: lastStepComponentId
                  });

                  return;
                }
                if (!sourceId || !sourceParentId) return;

                if (sourceParentId === swimlaneId) {
                  // When just rearranging steps within the same swimlane
                  // the length will not change, hence we need to adjust the index
                  onNodeMoved({
                    type: NodeType.STEP,
                    nodeId: sourceId,
                    sourceParentId,
                    targetParentId: swimlaneId,
                    index: steps.length - 1
                  });
                } else {
                  // When moving step from one swimlane to another
                  // the length will change, hence we need to take current length as index
                  onNodeMoved({
                    type: NodeType.STEP,
                    nodeId: sourceId,
                    sourceParentId,
                    targetParentId: swimlaneId,
                    index: steps.length
                  });
                }

                // Select the node that was dragged
                const dragContext = getDragContext();
                if (dragContext.node?.props.key) {
                  onSelect?.(dragContext.node?.props.key);
                }

                handleOnDrop({ event });
              },
              onDropping: event => {
                return handleOnDropping({
                  event,
                  key: getAddStepNodeKey(swimlane.id),
                  dropType: DragType.STEP
                });
              },
              onDroppingOut: event => {
                return handleOnDroppingOut({
                  event,
                  key: getAddStepNodeKey(swimlane.id)
                });
              }
            }),
        children: steps.length
          ? steps
          : [
              readonly
                ? NoChildren({
                    key: getSwimlaneNodeKey(`Empty-${swimlaneId}`),
                    title: "Empty swimlane"
                  })
                : BoxNode({
                    key: getSwimlaneNodeKey(`Empty-${swimlaneId}`),
                    style: style.zeroHeight,
                    children: []
                  })
            ],
        title: swimlane.name,
        onDragStart: event => {
          handleDragStart({
            event,
            sourceId: swimlaneId,
            sourceParentId: process.process.id,
            sourceIndex: swimlaneIndex,
            sourceDragType: DragType.SWIMLANE
          });

          onComponentDrag(swimlaneKey);
        },
        onDropped: event => {
          const { sourceId, sourceParentId, dragType, subType, extras } =
            event.dragContextStore || {};
          if (!sourceId || !sourceParentId) return;

          if (dragType === DragType.TEMPLATE_STEP) {
            onTemplateDrop({
              templateId: extras?.templateId as string,
              templateType: subType as StudioTemplateTypes,
              parentId: swimlaneId,
              position: steps.length
            });
            return;
          }

          /**
           * If condition is needed here because due to "hover to expand",
           * several different drag types are technically allowed.
           */
          if (dragType === DragType.STEP) {
            if (sourceParentId === swimlaneId) {
              // don't allow dropping on the same swimlane, by dropping step
              // on the header
              return;
            }
            onNodeMoved({
              type: NodeType.STEP,
              nodeId: sourceId,
              sourceParentId,
              targetParentId: swimlaneId,
              index: steps.length
            });
          } else if (dragType === DragType.SWIMLANE) {
            onNodeMoved({
              type: NodeType.SWIMLANE,
              nodeId: sourceId,
              sourceParentId,
              targetParentId: process.process.id,
              index: swimlaneIndex
            });
          }

          // Select the node that was dragged
          const dragContext = getDragContext();
          if (dragContext.node?.props.key) {
            onSelect?.(dragContext.node?.props.key);
          }

          handleOnDrop({ event });
        },
        onClick: event => {
          onSelect?.(swimlaneKey);
        },
        onCollapseToggle: handleCollapseToggle.bind(null, swimlaneKey),
        onDropping: event => {
          // On Dropping must check whether the dragged item is a child-type
          // of the current component, in this case Fields and Steps can trigger
          // auto-expansion of collapsed Swimlanes.
          const { dragContextStore } = event;
          if (dragContextStore?.sourceParentId === swimlaneId) {
            // don't allow dropping on the same swimlane, by dropping step
            // on the header
            return;
          }

          let isHandled = false;
          if (
            dragContextStore?.dragType &&
            AUTO_EXPAND_SWIMLANE_DROP_TYPES.includes(
              dragContextStore.dragType
            ) &&
            collapsed.includes(swimlaneKey)
          ) {
            onDropping?.(swimlaneKey);
            isHandled = true;
          }
          const isDropHandled = handleOnDropping({
            event,
            key: swimlaneKey,
            dropType: DragType.SWIMLANE
          });
          return isHandled || isDropHandled;
        },
        onDroppingOut: event => {
          onDroppingOut?.(swimlaneKey);
          return handleOnDroppingOut({
            event,
            key: swimlaneKey
          });
        }
      });
    }
  );

  if (!readonly) {
    children.push(
      AddSwimlane({
        key: getAddSwimlaneNodeKey(process.process.id),
        theme,
        isDroppingAbove: droppingOverSwimlaneIndex === children.length,
        onClick: () => {
          onNewNode({ type: NodeType.SWIMLANE, parentId: process.process.id });
        },
        onDropped: event => {
          const { sourceId, sourceParentId } = event.dragContextStore || {};
          if (!sourceId || !sourceParentId) return;

          onNodeMoved({
            type: NodeType.SWIMLANE,
            nodeId: sourceId,
            sourceParentId,
            targetParentId: process.process.id,
            index: process.process.swimlaneSequence.length - 1
          });

          // Select the node that was dragged
          const dragContext = getDragContext();
          if (dragContext.node?.props.key) {
            onSelect?.(dragContext.node?.props.key);
          }

          handleOnDrop({ event });
        },
        onDropping: event => {
          return handleOnDropping({
            event,
            key: getAddSwimlaneNodeKey(process.process.id),
            dropType: DragType.SWIMLANE
          });
        },
        onDroppingOut: event => {
          return handleOnDroppingOut({
            event,
            key: getAddSwimlaneNodeKey(process.process.id)
          });
        }
      })
    );

    if (droppingOverSwimlaneIndex !== -1) {
      children.splice(droppingOverSwimlaneIndex, 0, VerticalDropIndicator());
    }
  } else {
    if (process.process.swimlaneSequence.length === 0) {
      children.push(EmptyProcess());
    }
  }

  return BoxNode({
    key: getProcessNodeKey(`${process.process.id}`),
    style: style.process.container,
    eventHandlers: {
      onClick: () => {
        onDeselect?.();
      },
      onDropped: event => {
        const { dragType, extras, subType } = event.dragContextStore || {};

        if (
          dragType === DragType.TEMPLATE_SWIMLANE ||
          dragType === DragType.TEMPLATE_MODULE ||
          dragType === DragType.TEMPLATE_PROCESS
        ) {
          // parent and component Id are not required here for these template types.
          onTemplateDrop({
            templateId: extras?.templateId as string,
            templateType: subType as StudioTemplateTypes,
            position: process.process.swimlaneSequence.length
          });
          return;
        }
      }
    },
    dropTypes: [
      DragType.TEMPLATE_SWIMLANE,
      DragType.TEMPLATE_MODULE,
      DragType.TEMPLATE_PROCESS
    ],
    children: isSDOEnabled ? [...children] : children
  });
};
